import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Avatar,
  Tooltip,
  IconButton,
  Stack,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack,
  Block,
  LockOpen,
  DeleteOutline,
  EditOutlined,
  PhoneAndroid,
  Email,
  CalendarToday,
  Wc,
  Badge,
  AccessTime,
  WarningAmber,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import userService, { IUser } from "../../../services/user.service";
import { useAlert } from "../../../context/AlertContext";
import CustomerWashingHistory from "./CustomerWashingHistory";

function a11yProps(index: number) {
  return {
    id: `user-detail-tab-${index}`,
    "aria-controls": `user-detail-tabpanel-${index}`,
  };
}

function initials(user: IUser) {
  return `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

function avatarColor(id: string) {
  const palette = ["#1976d2", "#388e3c", "#7b1fa2", "#d32f2f", "#0288d1", "#f57c00"];
  const idx = id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % palette.length;
  return palette[idx];
}

function StatusChip({ user }: { user: IUser }) {
  const { t } = useTranslation();
  if (user.isBlocked)
    return <Chip icon={<WarningAmber fontSize="small" />} label={t("userDetail.blocked")} color="warning" size="small" />;
  return null;
}

function Field({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", py: 1 }}>
      <Box sx={{ color: "text.disabled", mt: "2px", flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
          {value || <span style={{ color: "#bbb", fontWeight: 400 }}>—</span>}
        </Typography>
      </Box>
    </Box>
  );
}

function ActionBtn({
  label,
  icon,
  borderColor,
  hoverBg,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  borderColor: string;
  hoverBg: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          disabled={disabled}
          onClick={onClick}
          sx={{
            border: 1,
            borderColor,
            color: borderColor,
            borderRadius: 2,
            px: 1.5,
            gap: 0.5,
            "&:hover": { bgcolor: hoverBg },
          }}
        >
          {icon}
          <Typography variant="body2" fontWeight={600} sx={{ ml: 0.5 }}>
            {label}
          </Typography>
        </IconButton>
      </span>
    </Tooltip>
  );
}

interface EditForm {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  dateOfBirth: string;
}

function toEditForm(user: IUser): EditForm {
  return {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    email: user.email ?? "",
    gender: user.gender ?? "",
    dateOfBirth: user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : "",
  };
}

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  const [user, setUser] = React.useState<IUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editForm, setEditForm] = React.useState<EditForm | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  React.useEffect(() => {
    if (userId) load();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    try {
      setLoading(true);
      setError(null);
      setUser(await userService.getUserById(userId!));
    } catch (e: any) {
      setError(e.message || "Failed to load user");
    } finally {
      setLoading(false);
    }
  }

  function openEdit() {
    if (!user) return;
    setEditForm(toEditForm(user));
    setEditOpen(true);
  }

  async function handleEditSave() {
    if (!user || !editForm) return;
    setBusy(true);
    try {
      await userService.updateUser(user._id, {
        firstName: editForm.firstName || undefined,
        lastName: editForm.lastName || undefined,
        email: editForm.email || undefined,
        gender: (editForm.gender as IUser["gender"]) || undefined,
        dateOfBirth: editForm.dateOfBirth || undefined,
      });
      setAlert("success", t("users.updatedSuccess"));
      setEditOpen(false);
      await load();
    } catch (e: any) {
      setAlert("error", e.message || t("users.updateFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleBlockToggle() {
    if (!user) return;
    setBusy(true);
    try {
      if (user.isBlocked) {
        await userService.unblockUser(user._id);
        setAlert("success", t("users.unblocked"));
      } else {
        await userService.blockUser(user._id);
        setAlert("success", t("users.blocked"));
      }
      await load();
    } catch (e: any) {
      setAlert("error", e.message || t("users.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!user) return;
    setBusy(true);
    try {
      await userService.deleteUser(user._id);
      setAlert("success", t("users.deleted"));
      navigate("/dashboard/manage-users");
    } catch (e: any) {
      setAlert("error", e.message || t("users.deleteFailed"));
      setDeleteOpen(false);
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || t("userDetail.userNotFound")}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/dashboard/manage-users")}>
          {t("nav.users")}
        </Button>
      </Box>
    );
  }

  const bg = avatarColor(user._id);

  return (
    <Box sx={{ maxWidth: 860, mx: "auto", py: 3, px: { xs: 1, sm: 0 } }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/dashboard/manage-users")}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          {t("nav.users")}
        </Button>

        <Stack direction="row" spacing={1}>
          <ActionBtn
            label={t("userDetail.edit")}
            icon={<EditOutlined fontSize="small" />}
            borderColor="primary.main"
            hoverBg="primary.50"
            disabled={busy}
            onClick={openEdit}
          />
          <ActionBtn
            label={user.isBlocked ? t("userDetail.unblock") : t("userDetail.block")}
            icon={user.isBlocked ? <LockOpen fontSize="small" /> : <Block fontSize="small" />}
            borderColor={user.isBlocked ? "success.main" : "warning.main"}
            hoverBg={user.isBlocked ? "success.50" : "warning.50"}
            disabled={busy}
            onClick={handleBlockToggle}
          />
          <ActionBtn
            label={t("userDetail.delete")}
            icon={<DeleteOutline fontSize="small" />}
            borderColor="error.main"
            hoverBg="error.50"
            disabled={busy}
            onClick={() => setDeleteOpen(true)}
          />
        </Stack>
      </Box>

      <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 2 }}>
        <Box
          sx={{
            height: 90,
            background: `linear-gradient(135deg, ${bg}22 0%, ${bg}44 100%)`,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        />
        <CardContent sx={{ pt: 0, pb: "24px !important" }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mt: -4, mb: 2 }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: bg,
                fontSize: 26,
                fontWeight: 700,
                border: "3px solid white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {initials(user)}
            </Avatar>
            <StatusChip user={user} />
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 11 }}>
            {user._id}
          </Typography>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_e, newValue) => setTab(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("userDetail.tabInfo")} {...a11yProps(0)} />
        <Tab label={t("userDetail.tabWashingHistory")} {...a11yProps(1)} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{t("userDetail.contact")}</Typography>
                <Divider sx={{ my: 1 }} />
                <Field icon={<PhoneAndroid fontSize="small" />} label={t("userDetail.phone")} value={`${user.country_code} ${user.phone}`} />
                <Field icon={<Email fontSize="small" />} label={t("userDetail.email")} value={user.email} />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{t("userDetail.personal")}</Typography>
                <Divider sx={{ my: 1 }} />
                <Field icon={<Wc fontSize="small" />} label={t("userDetail.gender")} value={user.gender} />
                <Field icon={<CalendarToday fontSize="small" />} label={t("userDetail.dateOfBirth")} value={user.dateOfBirth ? format(new Date(user.dateOfBirth), "MMM dd, yyyy") : null} />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{t("userDetail.account")}</Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={0}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Field icon={<Badge fontSize="small" />} label={t("userDetail.role")} value={user.userType} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Field icon={<AccessTime fontSize="small" />} label={t("userDetail.memberSince")} value={user.createdAt ? format(new Date(user.createdAt), "MMM dd, yyyy") : null} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Field icon={<AccessTime fontSize="small" />} label={t("userDetail.lastUpdated")} value={user.updatedAt ? format(new Date(user.updatedAt), "MMM dd, yyyy") : null} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <CustomerWashingHistory userId={user._id} name={`${user.firstName} ${user.lastName}`} />
      )}

      <Dialog open={editOpen} onClose={() => !busy && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("userDetail.editTitle")}</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {editForm && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("common.firstName")}
                  fullWidth
                  size="small"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("common.lastName")}
                  fullWidth
                  size="small"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t("userDetail.email")}
                  fullWidth
                  size="small"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("userDetail.gender")}</InputLabel>
                  <Select
                    label={t("userDetail.gender")}
                    value={editForm.gender}
                    onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>{t("userDetail.notSpecified")}</em>
                    </MenuItem>
                    <MenuItem value="Male">{t("userDetail.male")}</MenuItem>
                    <MenuItem value="Female">{t("userDetail.female")}</MenuItem>
                    <MenuItem value="Other">{t("userDetail.other")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("userDetail.dateOfBirth")}
                  fullWidth
                  size="small"
                  type="date"
                  value={editForm.dateOfBirth}
                  onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleEditSave} disabled={busy}>
            {busy ? t("userDetail.saving") : t("common.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("userDetail.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("userDetail.deleteConfirm", { name: `${user.firstName} ${user.lastName}` })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={busy}>
            {busy ? t("userDetail.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
